import type {ClientInteractionFailure, ClientInteractionUnknown} from './client-interaction-contract'
import type {RecoveryReason, RecoveryStage} from './client-recovery-contract'
import type {ResultPresentationFailure, ResultPresentationUnknown} from './result-presentation-contract'
import type {UiReadinessFailure} from './ui-readiness-contract'

// Outcome counters always carry both keys, with `none` where a reason does not apply, so every
// series has the same tag set. Detailed reasons stay in Hydro.
export const FAILURE_REASONS = [
  'bundle',
  'initialization',
  'state',
  'handoff',
  'binding',
  'controls',
  'timeout',
  'frontend_error',
  'preparation',
  'payload',
  'transport',
  'rejected',
  'render',
  'reconnect',
  'pending_work',
  'tools',
  'dispatch',
  'turn_error',
  'other',
  'none',
] as const
export type FailureReason = (typeof FAILURE_REASONS)[number]

export const UNRESOLVED_REASONS = [
  'page_exit',
  'hidden',
  'disposed',
  'unsupported',
  'missing_evidence',
  'cancelled',
  'no_content',
  'other',
  'none',
] as const
export type UnresolvedReason = (typeof UNRESOLVED_REASONS)[number]

export interface OutcomeReasons {
  failure_reason: FailureReason
  unresolved_reason: UnresolvedReason
}

export const NO_REASONS: OutcomeReasons = {failure_reason: 'none', unresolved_reason: 'none'}

export function failed(reason: FailureReason): OutcomeReasons {
  return {failure_reason: reason, unresolved_reason: 'none'}
}

export function unresolved(reason: UnresolvedReason): OutcomeReasons {
  return {failure_reason: 'none', unresolved_reason: reason}
}

const READINESS_FAILURES: Record<UiReadinessFailure, FailureReason> = {
  registration: 'bundle',
  bootstrap: 'initialization',
  hydration: 'state',
  handoff: 'handoff',
  session_mismatch: 'binding',
  role_mismatch: 'binding',
  controls_blocked: 'controls',
  product_timeout: 'timeout',
  frontend_error: 'frontend_error',
}

export function readinessFailureReason(reason: UiReadinessFailure | undefined): FailureReason {
  return (reason && READINESS_FAILURES[reason]) || 'other'
}

const INTERACTION_FAILURES: Record<ClientInteractionFailure, FailureReason> = {
  preparation_failed: 'preparation',
  attachment_loss: 'preparation',
  session_mismatch: 'binding',
  payload_mismatch: 'payload',
  transport_failed: 'transport',
  handler_rejected: 'rejected',
}

export function interactionFailureReason(reason: ClientInteractionFailure | undefined): FailureReason {
  return (reason && INTERACTION_FAILURES[reason]) || 'other'
}

const INTERACTION_UNKNOWNS: Record<ClientInteractionUnknown, UnresolvedReason> = {
  hidden: 'hidden',
  not_started: 'missing_evidence',
  visibility_unsupported: 'unsupported',
  observer_failed: 'unsupported',
  observation_capacity: 'other',
  feedback_unobserved: 'missing_evidence',
  input_start_unverified: 'missing_evidence',
  handoff_unobserved: 'missing_evidence',
  payload_unverified: 'missing_evidence',
  session_unverified: 'missing_evidence',
  stop_target_unverified: 'missing_evidence',
  stop_target_changed: 'cancelled',
  cancelled: 'cancelled',
  superseded: 'cancelled',
  reset: 'disposed',
  disposed: 'disposed',
  page_exit: 'page_exit',
}

export function interactionUnresolvedReason(reason: ClientInteractionUnknown | undefined): UnresolvedReason {
  return (reason && INTERACTION_UNKNOWNS[reason]) || 'other'
}

const PRESENTATION_FAILURES: Record<ResultPresentationFailure, FailureReason> = {
  render_failed: 'render',
  binding_mismatch: 'binding',
}

export function presentationFailureReason(reason: ResultPresentationFailure | undefined): FailureReason {
  return (reason && PRESENTATION_FAILURES[reason]) || 'other'
}

const PRESENTATION_UNKNOWNS: Record<ResultPresentationUnknown, UnresolvedReason> = {
  hidden: 'hidden',
  offscreen: 'hidden',
  visibility_unsupported: 'unsupported',
  observer_failed: 'unsupported',
  paint_unsupported: 'unsupported',
  paint_unobserved: 'missing_evidence',
  stale_paint: 'missing_evidence',
  content_unobserved: 'missing_evidence',
  terminal_unobserved: 'missing_evidence',
  controls_unobserved: 'missing_evidence',
  stale_controls: 'missing_evidence',
  role_unknown: 'missing_evidence',
  binding_unknown: 'missing_evidence',
  disposed: 'disposed',
  page_exit: 'page_exit',
  replaced: 'disposed',
  observation_capacity: 'other',
}

export function presentationUnresolvedReason(reason: ResultPresentationUnknown | undefined): UnresolvedReason {
  return (reason && PRESENTATION_UNKNOWNS[reason]) || 'other'
}

const RECOVERY_STAGE_FAILURES: Record<RecoveryStage, FailureReason> = {
  reconnect: 'reconnect',
  state_application: 'state',
  pending_commands: 'pending_work',
  client_tools: 'tools',
  controls: 'controls',
}

export function recoveryFailureReason(stage: RecoveryStage | undefined): FailureReason {
  return (stage && RECOVERY_STAGE_FAILURES[stage]) || 'other'
}

const RECOVERY_UNKNOWNS: Partial<Record<RecoveryReason, UnresolvedReason>> = {
  prerequisite_unavailable: 'missing_evidence',
  prestate_unknown: 'missing_evidence',
  revision_unknown: 'missing_evidence',
  item_unresolved: 'missing_evidence',
  role_unknown: 'missing_evidence',
  controls_unobserved: 'missing_evidence',
  state_unverified: 'missing_evidence',
  clock_unresolved: 'missing_evidence',
  entry_unverified: 'missing_evidence',
  visibility_interrupted: 'hidden',
  view_detached: 'disposed',
  disposed: 'disposed',
}

export function recoveryUnresolvedReason(reason: RecoveryReason | undefined): UnresolvedReason {
  return (reason && RECOVERY_UNKNOWNS[reason]) || 'other'
}
